import { useMemo } from "react"

import styles from "./YearMonthSelector.module.scss"

import {
	formatYearMonth,
	formatYearMonthLabel,
	generateYearMonthOptions,
} from "@/company_admin/utils/yearMonth"
import { getJstFullYear, getJstMonth } from "@/utils/jst_date"

type YearMonthSelectorProps = {
	selectedYearMonth: string // "YYYY-MM" format
	onSelectYearMonth: (ym: string) => void
}

const YearMonthSelector = ({
	selectedYearMonth,
	onSelectYearMonth,
}: YearMonthSelectorProps) => {
	const today = useMemo(() => new Date(), [])
	const options = useMemo(() => generateYearMonthOptions(today), [today])

	const currentYear = getJstFullYear(today)
	const currentMonth = getJstMonth(today) + 1

	const getOptionLabel = (ym: { year: number; month: number }): string => {
		const base = formatYearMonthLabel(ym)
		if (ym.year === currentYear && ym.month === currentMonth) {
			return `${base}（今月）`
		}
		// 来月
		const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1
		const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear
		if (ym.year === nextYear && ym.month === nextMonth) {
			return `${base}（来月）`
		}
		// 先月
		const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1
		const prevYear = currentMonth === 1 ? currentYear - 1 : currentYear
		if (ym.year === prevYear && ym.month === prevMonth) {
			return `${base}（先月）`
		}
		return base
	}

	const getOptionClassName = (ym: { year: number; month: number }): string => {
		if (ym.year === currentYear && ym.month === currentMonth) {
			return styles.option_current
		}
		const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1
		const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear
		if (ym.year === nextYear && ym.month === nextMonth) {
			return styles.option_next
		}
		const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1
		const prevYear = currentMonth === 1 ? currentYear - 1 : currentYear
		if (ym.year === prevYear && ym.month === prevMonth) {
			return styles.option_prev
		}
		return styles.option_past
	}

	return (
		<div className={styles.year_month_selector}>
			<select
				className={styles.select}
				value={selectedYearMonth}
				onChange={(e) => onSelectYearMonth(e.target.value)}
			>
				{options.map((ym) => {
					const value = formatYearMonth(ym)
					return (
						<option
							key={value}
							value={value}
							className={getOptionClassName(ym)}
						>
							{getOptionLabel(ym)}
						</option>
					)
				})}
			</select>
		</div>
	)
}

export default YearMonthSelector
